/**
 * Selection Store
 *
 * 배치 선택 모드 상태 관리
 * 기존 BatchSelectionContext를 대체합니다.
 */

import { useMemo } from 'react';
import { create } from 'zustand';

// ============================================================================
// Types
// ============================================================================

export type SelectionItemType = 'track' | 'playlist' | 'version';

interface SelectionState {
  selectedIds: Set<string>;
  isSelectionMode: boolean;
  itemType: SelectionItemType | null;
}

interface SelectionActions {
  // 선택 모드 제어
  enableSelectionMode: (itemType: SelectionItemType) => void;
  disableSelectionMode: () => void;
  toggleSelectionMode: () => void;

  // 아이템 선택 제어
  selectItem: (id: string) => void;
  deselectItem: (id: string) => void;
  toggleItem: (id: string) => void;
  selectAll: (ids: string[]) => void;
  deselectAll: () => void;
  setSelection: (ids: string[]) => void;

  // 헬퍼
  isSelected: (id: string) => boolean;
}

type SelectionStore = SelectionState & SelectionActions;

// ============================================================================
// Initial State
// ============================================================================

const initialState: SelectionState = {
  selectedIds: new Set(),
  isSelectionMode: false,
  itemType: null,
};

// ============================================================================
// Store
// ============================================================================

export const useSelectionStore = create<SelectionStore>()((set, get) => ({
  // State
  ...initialState,

  // 선택 모드 제어
  enableSelectionMode: (itemType) =>
    set({
      isSelectionMode: true,
      itemType,
      selectedIds: new Set(),
    }),

  disableSelectionMode: () =>
    set({
      isSelectionMode: false,
      itemType: null,
      selectedIds: new Set(),
    }),

  toggleSelectionMode: () =>
    set((state) => ({
      isSelectionMode: !state.isSelectionMode,
      selectedIds: new Set(),
    })),

  // 아이템 선택 제어
  selectItem: (id) =>
    set((state) => {
      const newSet = new Set(state.selectedIds);
      newSet.add(id);
      return { selectedIds: newSet };
    }),

  deselectItem: (id) =>
    set((state) => {
      const newSet = new Set(state.selectedIds);
      newSet.delete(id);
      return { selectedIds: newSet };
    }),

  toggleItem: (id) =>
    set((state) => {
      const newSet = new Set(state.selectedIds);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return { selectedIds: newSet };
    }),

  selectAll: (ids) => set({ selectedIds: new Set(ids) }),

  deselectAll: () => set({ selectedIds: new Set() }),

  setSelection: (ids) => set({ selectedIds: new Set(ids) }),

  // 헬퍼
  isSelected: (id) => get().selectedIds.has(id),
}));

// ============================================================================
// Hook (기존 useBatchSelection 호환)
// ============================================================================

/**
 * useBatchSelection 훅
 *
 * 기존 BatchSelectionContext의 useBatchSelection과 동일한 API를 제공합니다.
 *
 * @example
 * const { state, toggleItem, selectedCount, selectedIds } = useBatchSelection();
 */
export function useBatchSelection() {
  const store = useSelectionStore();

  // OPTIMIZATION: Set→Array 변환을 메모이제이션하여 매 렌더마다 새 배열 생성 방지
  const selectedIds = useMemo(() => Array.from(store.selectedIds), [store.selectedIds]);

  return {
    state: {
      selectedItems: store.selectedIds,
      isSelectionMode: store.isSelectionMode,
      itemType: store.itemType,
    },
    enableSelectionMode: store.enableSelectionMode,
    disableSelectionMode: store.disableSelectionMode,
    toggleSelectionMode: store.toggleSelectionMode,
    selectItem: store.selectItem,
    deselectItem: store.deselectItem,
    toggleItem: store.toggleItem,
    selectAll: store.selectAll,
    deselectAll: store.deselectAll,
    setSelection: store.setSelection,
    isSelected: store.isSelected,
    selectedCount: store.selectedIds.size,
    selectedIds,
  };
}

/**
 * useBatchSelectionOptional 훅
 *
 * Provider 없이도 사용 가능한 버전 (항상 정의됨)
 */
export function useBatchSelectionOptional() {
  return useBatchSelection();
}

// ============================================================================
// Selectors
// ============================================================================

export const selectIsSelectionMode = (state: SelectionStore) => state.isSelectionMode;
export const selectSelectedCount = (state: SelectionStore) => state.selectedIds.size;
export const selectItemType = (state: SelectionStore) => state.itemType;
/** 주의: 이 셀렉터는 매 호출마다 새 배열 생성 — useShallow와 함께 사용하거나 useBatchSelection().selectedIds 활용 */
export const selectSelectedIds = (state: SelectionStore) => Array.from(state.selectedIds);
